import { LitElement, html, css } from 'lit';
import { customElement } from 'lit/decorators.js';
import { isOffline, onConnectivityChange } from '../services/pwa.js';

/**
 * Wraps a call-to-action that only works with a live network connection (e.g. the home hero's "Go to
 * the lobby" link) and hides it while the device is offline, so the user isn't offered an action that
 * would just dead-end on the offline-fallback page.
 *
 * Children are projected through a <slot> and stay in the light DOM, so the page's global button styles
 * still apply, and `display: contents` makes the wrapper layout-transparent — the slotted link behaves
 * exactly like a sibling plain <a> in the flex CTA row. The server emits the link as a child (the no-JS
 * fallback); with no scripting the element is unknown and simply shows its children (assume online).
 */
@customElement('gc-online-only')
export class GcOnlineOnly extends LitElement {
  static override styles = css`
    :host { display: contents; }
    :host([hidden]) { display: none; }
  `;

  private unsubscribe?: () => void;

  override connectedCallback(): void {
    super.connectedCallback();
    this.syncHidden(!isOffline());
    this.unsubscribe = onConnectivityChange((online) => this.syncHidden(online));
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.unsubscribe?.();
  }

  private syncHidden(online: boolean): void {
    this.hidden = !online;
  }

  override render() {
    return html`<slot></slot>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gc-online-only': GcOnlineOnly;
  }
}
